import { type FormEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ROLES } from '@bcc/shared';
import type { GetStartedContentInput } from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { apiUrl } from '../../lib/apiUrl';
import { useAuth } from '../../contexts/AuthContext';

function pairsToText(rows: { label: string; value: string }[] | undefined): string {
  if (!rows?.length) return '';
  return rows.map((r) => `${r.label}\t${r.value}`).join('\n');
}

function textToPairs(text: string): { label: string; value: string }[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const tab = line.split('\t');
      if (tab.length >= 2) return { label: tab[0]!, value: tab.slice(1).join('\t') };
      const colon = line.indexOf(':');
      if (colon > 0) return { label: line.slice(0, colon).trim(), value: line.slice(colon + 1).trim() };
      return { label: line, value: '' };
    });
}

export function ActiveDealEditor() {
  const { session, role } = useAuth();
  const token = session?.access_token;
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [projSummaryText, setProjSummaryText] = useState('');
  const [keyAssumpText, setKeyAssumpText] = useState('');
  const [form, setForm] = useState<Partial<GetStartedContentInput>>({});

  if (role !== ROLES.ADMIN) {
    return <Navigate to="/admin/crm" replace />;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(apiUrl('/api/public/site-content/get_started'));
        const j = (await r.json()) as { value: Record<string, unknown> };
        if (cancelled) return;
        const v = j.value as Partial<GetStartedContentInput>;
        setForm(v);
        setProjSummaryText(pairsToText(v.underwritingProjectSummary));
        setKeyAssumpText(pairsToText(v.underwritingKeyAssumptions));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSaved(false);
    const payload: Partial<GetStartedContentInput> = {
      ...form,
      underwritingProjectSummary: textToPairs(projSummaryText),
      underwritingKeyAssumptions: textToPairs(keyAssumpText),
    };
    await apiFetch('/api/admin/site-content/get_started', {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload),
    });
    setSaved(true);
  }

  function setField<K extends keyof GetStartedContentInput>(key: K, value: GetStartedContentInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  if (loading) {
    return <p className="text-sm text-[var(--home-muted-on-dark)]">Loading…</p>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--home-text-on-dark)]">Active deal &amp; Get Started</h1>
        <p className="mt-1 text-sm text-[var(--home-muted-on-dark)]">
          Updates the spotlight card and headline fields on /getstarted. Summary rows: one per line,
          Label [tab] Value or Label: Value.
        </p>
      </div>

      {saved ? <p className="text-sm text-emerald-300">Saved.</p> : null}

      <form className={`space-y-4 p-6 ${SITE_GLASS_CARD}`} onSubmit={onSubmit}>
        <h2 className="text-sm font-semibold text-[var(--home-text-on-dark)]">Hero</h2>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Location tag</span>
          <input
            value={form.heroLocationTag ?? ''}
            onChange={(e) => setField('heroLocationTag', e.target.value)}
            className={`w-full ${SITE_FORM_CONTROL}`}
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Headline line 1</span>
          <input value={form.heroTitleLine1 ?? ''} onChange={(e) => setField('heroTitleLine1', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Headline accent</span>
          <input value={form.heroTitleAccent ?? ''} onChange={(e) => setField('heroTitleAccent', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Subtitle paragraph</span>
          <textarea rows={2} value={form.heroSubtitle ?? ''} onChange={(e) => setField('heroSubtitle', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Min Class A</span>
            <input value={form.minClassA ?? ''} onChange={(e) => setField('minClassA', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Min Class B</span>
            <input value={form.minClassB ?? ''} onChange={(e) => setField('minClassB', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
          </label>
        </div>

        <h2 className="pt-4 text-sm font-semibold text-[var(--home-text-on-dark)]">Active deal spotlight</h2>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Title</span>
          <input value={form.spotlightTitle ?? ''} onChange={(e) => setField('spotlightTitle', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Subtitle</span>
          <input value={form.spotlightSubtitle ?? ''} onChange={(e) => setField('spotlightSubtitle', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Image path (URL or /public path)</span>
          <input
            value={form.spotlightImagePath ?? ''}
            onChange={(e) => setField('spotlightImagePath', e.target.value)}
            className={`w-full ${SITE_FORM_CONTROL}`}
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Deal class pill</span>
          <input value={form.dealClass ?? ''} onChange={(e) => setField('dealClass', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Strategy</span>
          <textarea rows={2} value={form.strategy ?? ''} onChange={(e) => setField('strategy', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Units</span>
          <input
            type="number"
            value={form.units ?? ''}
            onChange={(e) => setField('units', e.target.value ? Number(e.target.value) : undefined)}
            className={`w-full ${SITE_FORM_CONTROL}`}
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Location ranking</span>
          <input value={form.locationRanking ?? ''} onChange={(e) => setField('locationRanking', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Population growth (5 yr)</span>
          <input value={form.populationGrowth5Yr ?? ''} onChange={(e) => setField('populationGrowth5Yr', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>

        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Project summary rows</span>
          <textarea rows={8} value={projSummaryText} onChange={(e) => setProjSummaryText(e.target.value)} className={`w-full font-mono text-xs ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Key assumptions rows</span>
          <textarea rows={6} value={keyAssumpText} onChange={(e) => setKeyAssumpText(e.target.value)} className={`w-full font-mono text-xs ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Underwriting footnote</span>
          <textarea rows={2} value={form.underwritingFootnote ?? ''} onChange={(e) => setField('underwritingFootnote', e.target.value)} className={`w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Linked deal UUID (optional)</span>
          <input
            value={form.linkedDealId ?? ''}
            onChange={(e) => setField('linkedDealId', e.target.value || null)}
            className={`w-full ${SITE_FORM_CONTROL}`}
            placeholder="optional UUID from a published deal"
          />
        </label>

        <button type="submit" className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] hover:brightness-105">
          Save to Supabase
        </button>
      </form>
    </div>
  );
}
